import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddRounded, ArrowForwardRounded, ChevronRightRounded, SearchRounded, ExpandMoreRounded } from '@mui/icons-material';
import { ReinforcementStudent, Student, StudentWithConversation, TeacherClassItem } from '../types';
import {
  getStudentsForClass,
  getClassReinforcementReport,
  getSyllabusProgress,
  getTeacherCurriculum,
} from '../services/api';
import { resolveTeacherClassContext, saveTeacherClassContext } from '../utils/teacherClassContext';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import TeacherLayout from './teacher/TeacherLayout';

interface ClassDetailsState {
  school?: string;
  grade?: number | string;
  section?: string | null;
}

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
};

const classLabel = (grade: number, section?: string | null) =>
  `Grade ${grade}${section ? ` · ${section}` : ''}`;

const classKey = (grade: number | undefined, section?: string | null) =>
  `${grade ?? ''}::${section ?? ''}`;

const initials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Relative "active" label from an ISO timestamp: today / yesterday / N days ago / date.
const activeLabel = (iso?: string | null): string => {
  // No conversation at all = the student has never used the coach. Saying
  // "active recently" here painted all 3,600 freshly-onboarded accounts as
  // active, which is the opposite of what a facilitator needs to see.
  if (!iso) return 'not active yet';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'not active yet';
  const startOfDay = (d: Date) => {
    const c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c;
  };
  const diffDays = Math.round(
    (startOfDay(new Date()).getTime() - startOfDay(then).getTime()) / 86400000
  );
  if (diffDays <= 0) return 'active today';
  if (diffDays === 1) return 'active yesterday';
  if (diffDays < 7) return `active ${diffDays} days ago`;
  return `active ${then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
};

type CategoryKey = ReinforcementStudent['category'];

const CATEGORY_META: Record<
  Exclude<CategoryKey, 'unknown'>,
  { label: string; className: string }
> = {
  bright: { label: 'Ahead', className: 'bg-emerald-50 text-emerald-700' },
  hardworking: { label: 'On track', className: 'bg-sky-50 text-sky-700' },
  disengaged: { label: 'Needs support', className: 'bg-amber-50 text-amber-700' },
};

const ROLL_LABEL = (student: Student) =>
  student.roll_number != null ? `Roll ${student.roll_number}` : 'Roll —';

const INITIAL_VISIBLE = 5;

interface ClassCardData {
  count: number | null;
  done: number | null;
  total: number | null;
}

const ClassDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { teacher, token } = useTeacherAuth();

  const resolved = resolveTeacherClassContext((location.state as ClassDetailsState) || {});
  const school = resolved.school ?? teacher?.school;

  const resolvedGrade = useMemo(() => {
    if (typeof resolved.grade === 'number') return resolved.grade;
    if (typeof resolved.grade === 'string') {
      const parsed = Number(resolved.grade);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  }, [resolved.grade]);
  const resolvedSection = resolved.section ?? null;

  // The active class is local state so switching cards re-fetches the roster
  // without a full navigation.
  const [activeGrade, setActiveGrade] = useState<number | undefined>(resolvedGrade);
  const [activeSection, setActiveSection] = useState<string | null>(resolvedSection);

  const classes: TeacherClassItem[] = useMemo(() => teacher?.classes ?? [], [teacher]);

  // Redirect to class management if there is no usable class context at all.
  useEffect(() => {
    if (!school || (!activeGrade && classes.length === 0)) {
      navigate('/teacher-view', { replace: true });
    }
  }, [school, activeGrade, classes.length, navigate]);

  // If we have no active grade yet but the teacher has classes, adopt the first.
  useEffect(() => {
    if (activeGrade == null && classes.length > 0) {
      setActiveGrade(classes[0].grade);
      setActiveSection(classes[0].section ?? null);
    }
  }, [activeGrade, classes]);

  // Persist the active class context.
  useEffect(() => {
    if (school && activeGrade != null) {
      saveTeacherClassContext({ school, grade: activeGrade, section: activeSection });
    }
  }, [school, activeGrade, activeSection]);

  const subjectsLabel = useMemo(() => {
    const subs = teacher?.subjects ?? [];
    return subs.map((s) => SUBJECT_LABELS[s] || s).join(', ');
  }, [teacher]);

  // --- Roster for the active class ---
  const [students, setStudents] = useState<StudentWithConversation[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportStudents, setReportStudents] = useState<ReinforcementStudent[] | null>(null);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!school || activeGrade == null) return;
    let mounted = true;
    setIsLoading(true);
    setError(null);
    setShowAll(false);
    getStudentsForClass(school, activeGrade, activeSection)
      .then((rows) => {
        if (mounted) setStudents(rows);
      })
      .catch((err) => {
        console.error('Failed to fetch students for class:', err);
        if (mounted) setError('Failed to fetch students. Please try again.');
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [school, activeGrade, activeSection]);

  // Category source: reinforcement report (cached, never force-refresh here).
  // Degrades to "no badge" when it fails or is missing.
  useEffect(() => {
    if (!token || activeGrade == null) {
      setReportStudents(null);
      return;
    }
    let mounted = true;
    getClassReinforcementReport(token, activeGrade, activeSection, false, undefined, undefined, school)
      .then((r) => {
        if (mounted) setReportStudents(r.report_data?.students ?? null);
      })
      .catch(() => {
        if (mounted) setReportStudents(null);
      });
    return () => {
      mounted = false;
    };
  }, [token, school, activeGrade, activeSection]);

  // Match a roster student to a reinforcement row by id, then by name.
  const categoryFor = (student: Student): Exclude<CategoryKey, 'unknown'> | null => {
    if (!reportStudents) return null;
    const byId = reportStudents.find((r) => r.student_id === student.id);
    const match =
      byId ||
      reportStudents.find(
        (r) => r.name && r.name.trim().toLowerCase() === (student.first_name || '').trim().toLowerCase()
      );
    if (!match || match.category === 'unknown') return null;
    return match.category;
  };

  // --- Per-class card data (count + syllabus progress), fetched in parallel ---
  const [cardData, setCardData] = useState<Record<string, ClassCardData>>({});

  useEffect(() => {
    if (!school || classes.length === 0) return;
    let mounted = true;

    classes.forEach((cls) => {
      const key = classKey(cls.grade, cls.section ?? null);
      // Student count.
      getStudentsForClass(school, cls.grade, cls.section ?? null)
        .then((rows) => {
          if (!mounted) return;
          setCardData((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? { count: null, done: null, total: null }), count: rows.length },
          }));
        })
        .catch(() => {
          /* degrade: leave count null */
        });

      if (token) {
        // Progress: done sub-topics / total sub-topics.
        Promise.all([
          getSyllabusProgress(token, cls.grade, cls.section ?? null).catch(() => null),
          getTeacherCurriculum(token, cls.grade).catch(() => null),
        ]).then(([progress, curriculum]) => {
          if (!mounted) return;
          const done = progress ? progress.items.length : null;
          const total = curriculum
            ? curriculum.subjects.reduce(
                (sum, subj) =>
                  sum + subj.chapters.reduce((c, ch) => c + (ch.sub_topics?.length ?? 0), 0),
                0
              )
            : null;
          setCardData((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? { count: null, done: null, total: null }), done, total: total || null },
          }));
        });
      }
    });

    return () => {
      mounted = false;
    };
  }, [school, classes, token]);

  const navState = {
    school,
    grade: activeGrade,
    section: activeSection ?? undefined,
  };

  const switchClass = (cls: TeacherClassItem) => {
    if (school) {
      saveTeacherClassContext({ school, grade: cls.grade, section: cls.section ?? null });
    }
    setActiveGrade(cls.grade);
    setActiveSection(cls.section ?? null);
    setSearch('');
  };

  const filteredStudents = useMemo(() => {
    if (!students) return [];
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((entry) =>
      (entry.student.first_name || '').toLowerCase().includes(q)
    );
  }, [students, search]);

  const visibleStudents = showAll ? filteredStudents : filteredStudents.slice(0, INITIAL_VISIBLE);
  const studentCount = students?.length ?? 0;
  const activeClassLabel =
    activeGrade != null ? classLabel(activeGrade, activeSection) : 'Class';

  const openStudentReport = (student: Student) => {
    navigate('/student-report', {
      state: {
        student,
        school,
        grade: activeGrade,
        section: activeSection ?? undefined,
      },
    });
  };

  return (
    <TeacherLayout active="classes">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          {school && (
            <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-400">
              {school}
            </p>
          )}
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            Classes
          </h1>
          <p className="mt-1.5 max-w-[62ch] text-sm text-slate-500">
            Pick a class to see its students, or add one you teach.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/teacher-view', { state: navState })}
          className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
        >
          <AddRounded fontSize="small" /> Add a class
        </button>
      </div>

      {/* Class cards */}
      {classes.length > 0 && (
        <div className="mb-5 grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {classes.map((cls) => {
            const key = classKey(cls.grade, cls.section ?? null);
            const data = cardData[key];
            const isActive =
              cls.grade === activeGrade && (cls.section ?? null) === (activeSection ?? null);
            const pct =
              data?.done != null && data?.total != null && data.total > 0
                ? Math.min(100, Math.round((data.done / data.total) * 100))
                : null;
            const metaBits = [subjectsLabel].filter(Boolean);
            if (data?.count != null) metaBits.push(`${data.count} students`);
            return (
              <button
                key={key}
                type="button"
                onClick={() => switchClass(cls)}
                className={`rounded-2xl bg-white p-4 text-left transition-colors ${
                  isActive
                    ? 'border-2 border-violet-600 p-[15px]'
                    : 'border border-[#E9E6F4] hover:border-violet-200'
                }`}
                aria-current={isActive ? 'true' : undefined}
              >
                <span className="block text-lg font-bold text-slate-900">
                  {classLabel(cls.grade, cls.section ?? null)}
                </span>
                {metaBits.length > 0 && (
                  <span className="mt-1 block text-xs text-slate-500">{metaBits.join(' · ')}</span>
                )}
                {pct != null && data && (
                  <span className="mt-3.5 flex items-center gap-2.5">
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E9E6F4]">
                      <span
                        className="block h-full rounded-full bg-violet-600"
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                    <span className="text-xs font-semibold tabular-nums text-slate-500">
                      {data.done}/{data.total}
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Roster card */}
      <div className="rounded-2xl border border-[#E9E6F4] bg-white p-5 md:p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{activeClassLabel} — students</h2>
            <p className="mt-0.5 text-xs text-slate-400">
              {studentCount} student{studentCount === 1 ? '' : 's'} · tap a name for their report
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/class-reinforcement', { state: navState })}
            className="inline-flex flex-none items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline"
          >
            Whole-class report <ArrowForwardRounded sx={{ fontSize: 15 }} />
          </button>
        </div>

        {/* Search */}
        <div className="mb-3 flex items-center gap-2.5 rounded-xl border border-[#E9E6F4] bg-violet-50 px-3.5 py-2.5 text-slate-400">
          <SearchRounded fontSize="small" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search students"
            aria-label="Search students"
            className="w-full border-0 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
        </div>

        {isLoading && <p className="py-4 text-sm text-slate-500">Loading students…</p>}
        {error && <p className="py-4 text-sm text-rose-600">{error}</p>}

        {!isLoading && !error && students && (
          <>
            {filteredStudents.length === 0 ? (
              <p className="py-4 text-sm text-slate-500">
                {search ? 'No students match your search.' : 'No students found for this class.'}
              </p>
            ) : (
              <div className="flex flex-col">
                {visibleStudents.map(({ student, latest_conversation }, idx) => {
                  const category = categoryFor(student);
                  const badge = category ? CATEGORY_META[category] : null;
                  return (
                    <button
                      key={student.id}
                      type="button"
                      onClick={() => openStudentReport(student)}
                      className={`flex items-center gap-3.5 rounded-xl px-2 py-3 text-left transition-colors hover:bg-[#F1EFFA] ${
                        idx === 0 ? '' : 'border-t border-[#F1EFFA]'
                      }`}
                    >
                      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#F1EFFA] text-xs font-bold text-slate-500">
                        {initials(student.first_name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-slate-900">
                          {student.first_name}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {ROLL_LABEL(student)} · {activeLabel(latest_conversation?.updated_at)}
                        </span>
                      </span>
                      {badge && (
                        <span
                          className={`flex-none rounded-full px-2.5 py-1 text-xs font-bold ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      )}
                      <ChevronRightRounded className="flex-none text-slate-300" fontSize="small" />
                    </button>
                  );
                })}
              </div>
            )}

            {filteredStudents.length > INITIAL_VISIBLE && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline"
              >
                {showAll ? 'Show fewer' : `Show all ${filteredStudents.length} students`}
                <ExpandMoreRounded
                  fontSize="small"
                  className={showAll ? 'rotate-180 transition-transform' : 'transition-transform'}
                />
              </button>
            )}
          </>
        )}
      </div>
    </TeacherLayout>
  );
};

export default ClassDetails;
